import { stripMarkdown, formatShortDate } from './format';
import { getRiskLevel, getRiskStyle } from './risk';
import { sendExecutiveBriefEmail } from './emailActivity';
import { displayHeadline } from './displayHeadline';

function bestRecordForCompany(records, company) {
  if (!company) return null;
  const severityOrder = { high: 3, medium: 2, low: 1, unknown: 0 };
  return [...records]
    .filter(rec => rec.company === company)
    .sort((a, b) => {
      const diff = (severityOrder[getRiskLevel(b.severity)] || 0) - (severityOrder[getRiskLevel(a.severity)] || 0);
      if (diff !== 0) return diff;
      return new Date(b.published_at || 0) - new Date(a.published_at || 0);
    })[0] || null;
}

export function buildBriefItems(priorities, records, maxItems = 5) {
  const fromPriorities = (priorities?.companies || [])
    .map(p => {
      const top = p.insight?.top_priority;
      if (!top && !p.company) return null;
      const match = bestRecordForCompany(records, p.company)
        || records.find(r => r.company === p.company);
      const titleSeed = top?.headline || match?.title || '';
      const synthetic = {
        company: p.company,
        title: titleSeed,
        severity: top?.severity || match?.severity || 'medium',
        categories: match?.categories,
        entity_type: match?.entity_type,
      };
      const headline = displayHeadline(match || synthetic);
      const description = stripMarkdown(
        top?.why_now
        || match?.summary
        || match?.snippet
        || match?.content
        || top?.headline
        || '',
      ).trim();
      if (!headline && !description) return null;
      return {
        company: p.company,
        headline: headline || titleSeed || `${p.company}: development to review`,
        description,
        severity: top?.severity || match?.severity || 'medium',
        published_at: match?.published_at || null,
        record: match || synthetic,
      };
    })
    .filter(Boolean);

  if (fromPriorities.length >= maxItems) return fromPriorities.slice(0, maxItems);

  // Pad remaining slots with each *other* company's single top record.
  // Excluding companies already in fromPriorities stops the same story from
  // showing twice (once as the AI top-priority item, once as a raw record),
  // and capping to one record per company stops a single high-severity
  // company from filling every padding slot and crowding out everyone else.
  const usedCompanies = new Set(fromPriorities.map(item => item.company));
  const severityOrder = { high: 3, medium: 2, low: 1, unknown: 0 };
  const bestPerCompany = new Map();
  [...records]
    .filter(rec => rec.company && !usedCompanies.has(rec.company))
    .sort((a, b) => {
      const diff = (severityOrder[getRiskLevel(b.severity)] || 0) - (severityOrder[getRiskLevel(a.severity)] || 0);
      if (diff !== 0) return diff;
      return new Date(b.published_at || 0) - new Date(a.published_at || 0);
    })
    .forEach(rec => {
      if (!bestPerCompany.has(rec.company)) bestPerCompany.set(rec.company, rec);
    });

  const fromRecords = [...bestPerCompany.values()]
    .slice(0, maxItems - fromPriorities.length)
    .map(rec => ({
      company: rec.company,
      headline: displayHeadline(rec) || stripMarkdown(rec.title || '').trim() || `${rec.company}: development to review`,
      description: stripMarkdown(rec.summary || rec.snippet || rec.content || '').trim(),
      severity: rec.severity,
      published_at: rec.published_at,
      record: rec,
    }));

  return fromPriorities.length > 0
    ? [...fromPriorities, ...fromRecords].slice(0, maxItems)
    : fromRecords;
}

export function buildRiskOverview(records, priorities) {
  const counts = { high: 0, medium: 0, low: 0, unknown: 0 };
  records.forEach(r => {
    const level = getRiskLevel(r.severity);
    counts[level] = (counts[level] || 0) + 1;
  });

  const companyBreakdown = (priorities?.companies || []).map(c => ({
    company: c.company,
    high: c.severity_counts?.high || 0,
    medium: c.severity_counts?.medium || 0,
    low: c.severity_counts?.low || 0,
  }));

  return {
    high: counts.high,
    medium: counts.medium,
    low: counts.low,
    unknown: counts.unknown,
    total: records.length,
    companyBreakdown,
  };
}

export function composeExecutiveBrief({ priorities, records, maxItems = 5 }) {
  const briefItems = buildBriefItems(priorities, records, maxItems);
  const briefRecords = briefItems.map(item => item.record).filter(Boolean);
  const riskOverview = buildRiskOverview(briefRecords, priorities);
  const dateLabel = priorities?.date
    ? new Date(priorities.date).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    : new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const executiveSummary = (priorities?.executive_brief || '').trim()
    || briefItems.map((item, i) => `${i + 1}. ${item.company}: ${item.headline}`).join('\n')
    || 'No material developments in today\'s monitoring window.';

  return {
    dateLabel,
    briefItems,
    riskOverview,
    executiveSummary,
  };
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function briefFilename(ext) {
  const stamp = new Date().toISOString().slice(0, 10);
  return `corp-sentinel-executive-brief-${stamp}.${ext}`;
}

function truncateText(value, maxLength = 260) {
  const clean = stripMarkdown(String(value || '')).replace(/\s+/g, ' ').trim();
  if (clean.length <= maxLength) return clean;
  return `${clean.slice(0, maxLength).replace(/\s+\S*$/, '')}...`;
}

function buildExecutiveBriefHtml({ dateLabel, briefItems = [], riskOverview, executiveSummary }) {
  const summaryBullets = executiveSummary
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .slice(0, 2)
    .map(line => `<li>${escapeHtml(truncateText(line.replace(/^[-•]\s*/, ''), 180))}</li>`)
    .join('');

  const renderCard = (item) => {
    const level = getRiskLevel(item.severity);
    const style = getRiskStyle(item.severity);
    const risk = style.label.replace(' RISK', '');
    const rawCategory = item.category || item.record?.categories?.[0] || '';
    const category = rawCategory ? rawCategory.charAt(0).toUpperCase() + rawCategory.slice(1) : '';
    return `
      <article class="development ${level}">
        <div class="priority-head">
          <strong>${escapeHtml(item.company)}</strong>
          <span class="risk ${level}">${escapeHtml(risk)}</span>
        </div>
        <p class="headline">${escapeHtml(item.headline)}</p>
        <p class="desc">${escapeHtml(truncateText(item.description, 360))}</p>
        <div class="meta-row">
          ${item.published_at ? `<span>${escapeHtml(formatShortDate(item.published_at))}</span>` : '<span>Date not available</span>'}
          ${category ? `<span>${escapeHtml(category)}</span>` : ''}
          ${item.sourceCount > 1 ? `<span>${item.sourceCount} related articles grouped</span>` : ''}
        </div>
      </article>
    `;
  };

  const sectionConfig = [
    { level: 'high', title: 'Critical And High Priority Updates', empty: 'No critical or high priority developments in this overview.' },
    { level: 'medium', title: 'Medium Priority Updates', empty: 'No medium priority developments in this overview.' },
    { level: 'low', title: 'Low Priority Updates', empty: 'No low priority developments in this overview.' },
    { level: 'unknown', title: 'Other Updates', empty: 'No other developments in this overview.' },
  ];

  const developmentSections = sectionConfig
    .map(section => {
      const items = briefItems.filter(item => getRiskLevel(item.severity) === section.level);
      if (items.length === 0) return '';
      return `
        <section class="section">
          <h2>${section.title}</h2>
          ${items.map(renderCard).join('')}
        </section>
      `;
    })
    .join('');

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Corp Sentinel Executive Brief</title>
  <style>
    * { box-sizing: border-box; }
    @page { size: A4; margin: 10mm; }
    body {
      font-family: Inter, "Segoe UI", Arial, sans-serif;
      color: #172033;
      margin: 0;
      padding: 0;
      font-size: 10.5px;
      line-height: 1.35;
      background: #ffffff;
    }
    .page {
      max-width: 760px;
      margin: 0 auto;
    }
    header {
      border-bottom: 2px solid #1d4ed8;
      padding-bottom: 8px;
      margin-bottom: 8px;
      display: flex;
      justify-content: space-between;
      gap: 16px;
      align-items: flex-end;
    }
    .eyebrow { color: #2563eb; font-size: 9px; font-weight: 800; letter-spacing: 0.12em; text-transform: uppercase; margin: 0 0 2px; }
    header h1 { margin: 0; font-size: 19px; letter-spacing: -0.02em; }
    header p { margin: 3px 0 0; color: #64748b; font-size: 10px; }
    .confidential { color: #b91c1c; font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.08em; white-space: nowrap; }
    h2 { font-size: 10px; text-transform: uppercase; letter-spacing: 0.08em; color: #475569; margin: 0 0 6px; }
    .section { margin-bottom: 9px; }
    .snapshot { display: flex; flex-wrap: wrap; gap: 5px; margin-bottom: 9px; }
    .pill { border-radius: 999px; padding: 3px 7px; font-size: 9px; font-weight: 800; border: 1px solid #e2e8f0; background: #f8fafc; color: #475569; }
    .pill.high { color: #b91c1c; background: #fee2e2; border-color: #fecaca; }
    .pill.medium { color: #92400e; background: #fef3c7; border-color: #fde68a; }
    .pill.low { color: #166534; background: #dcfce7; border-color: #bbf7d0; }
    .summary {
      border: 1px solid #dbeafe;
      background: #eff6ff;
      border-radius: 8px;
      padding: 8px 10px;
    }
    ul { margin: 0; padding-left: 16px; }
    ul li { margin-bottom: 4px; }
    .development { border: 1px solid #e2e8f0; border-left: 4px solid #2563eb; border-radius: 9px; padding: 9px 11px; margin-bottom: 7px; break-inside: avoid; page-break-inside: avoid; }
    .development.high { border-left-color: #dc2626; }
    .development.medium { border-left-color: #d97706; }
    .development.low { border-left-color: #16a34a; }
    .development.unknown { border-left-color: #94a3b8; }
    .priority-head { margin-bottom: 4px; }
    .priority-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
    .priority-head strong { display: block; font-size: 12px; color: #0f172a; }
    .risk { border-radius: 999px; padding: 3px 7px; font-size: 9px; font-weight: 800; white-space: nowrap; }
    .risk.high { color: #b91c1c; background: #fee2e2; }
    .risk.medium { color: #92400e; background: #fef3c7; }
    .risk.low { color: #166534; background: #dcfce7; }
    .risk.unknown { color: #475569; background: #f1f5f9; }
    .headline { margin: 0 0 4px; font-size: 11.5px; font-weight: 700; color: #1e293b; }
    .desc { margin: 0; color: #475569; font-size: 10px; }
    .meta-row { display: flex; flex-wrap: wrap; gap: 7px; align-items: center; margin-top: 6px; padding-top: 5px; border-top: 1px solid #edf2f7; font-size: 9px; color: #64748b; }
    .empty { margin: 0; color: #94a3b8; font-size: 10.5px; border: 1px dashed #cbd5e1; border-radius: 10px; padding: 10px 12px; }
    footer { margin-top: 16px; border-top: 1px solid #e2e8f0; padding-top: 8px; display: flex; justify-content: space-between; gap: 12px; font-size: 9px; color: #94a3b8; }
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      a[href]::after { content: ""; }
    }
  </style>
</head>
<body>
  <main class="page">
    <header>
      <div>
        <p class="eyebrow">Corporate Intelligence</p>
        <h1>Overview Brief</h1>
        <p>${escapeHtml(dateLabel)} · Complete overview information from monitored intelligence</p>
      </div>
      <div class="confidential">Confidential</div>
    </header>

    <section class="section summary">
      <h2>AI Summary</h2>
      <ul>${summaryBullets || '<li>No material developments in this monitoring window.</li>'}</ul>
    </section>

    <div class="snapshot">
      <span class="pill high">High / Critical: ${riskOverview.high}</span>
      <span class="pill medium">Medium: ${riskOverview.medium}</span>
      <span class="pill low">Low: ${riskOverview.low}</span>
      <span class="pill">Brief items: ${briefItems.length}</span>
    </div>

    ${developmentSections || '<section class="section"><p class="empty">No overview developments available.</p></section>'}

    <footer>
      <span>Corp Sentinel · Altius Soft</span>
      <span>Generated ${escapeHtml(new Date().toLocaleString('en-IN'))}</span>
    </footer>
  </main>
</body>
</html>`;
}

export function exportExecutiveBriefPdf(brief) {
  const html = buildExecutiveBriefHtml(brief);

  if (typeof document === 'undefined' || !document.body) {
    return;
  }

  const downloadHtmlFallback = () => {
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = briefFilename('html');
    link.click();
    URL.revokeObjectURL(url);
  };

  const frame = document.createElement('iframe');
  frame.style.position = 'fixed';
  frame.style.right = '0';
  frame.style.bottom = '0';
  frame.style.width = '0';
  frame.style.height = '0';
  frame.style.border = '0';
  frame.setAttribute('title', 'Executive brief PDF export');
  document.body.appendChild(frame);

  const cleanup = () => {
    setTimeout(() => {
      if (frame.parentNode) frame.parentNode.removeChild(frame);
    }, 500);
  };

  const frameWindow = frame.contentWindow;
  const frameDocument = frame.contentDocument || frameWindow?.document;
  if (!frameWindow || !frameDocument) {
    cleanup();
    downloadHtmlFallback();
    return;
  }

  frameWindow.onafterprint = cleanup;
  frameDocument.open();
  frameDocument.write(html);
  frameDocument.close();

  setTimeout(() => {
    frameWindow.focus();
    frameWindow.print();
  }, 150);
}

export function sendExecutiveBriefToTeam(brief, recipient = '', subject = '') {
  return sendExecutiveBriefEmail(brief, recipient, subject);
}
